/**
 * 앱 다국어(한국어·영어·일본어·중국어) 초기화.
 * 기획: `docs/ops/app-i18n-plan.md` · 배치 시안: `docs/design/app-language-switcher-variants.html`
 *
 * - 손으로 쓴 작은 사전(`locales/{ko,en,ja,zh}.json`)은 번들에 싣는다.
 * - 자동 추출 사전(`locales/auto.*.json`, 네 벌 합계 약 2.8MB)은 **쓰는 언어만 따로 받는다**(2026-10-29).
 *   네 벌을 첫 화면 번들에 싣던 때 main.js 가 6.5MB 까지 커져 홈 로딩이 느려졌다.
 *   첫 화면은 `i18nReady`(지금 언어 + 한국어 사전)를 기다린 뒤 그린다 — `src/index.js`.
 * - 번역이 없는 키는 **한국어로 폴백**한다 — 빈 화면·키 노출 금지.
 * - 언어 판별/저장 규칙은 `src/lib/languagePreference.js`(순수 함수, 테스트 있음)에 있다.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import ko from "./locales/ko.json";
import en from "./locales/en.json";
import ja from "./locales/ja.json";
import zh from "./locales/zh.json";
import {
  DEFAULT_LANG,
  SUPPORTED_LANGS,
  HTML_LANG,
  resolveInitialLang,
  readStoredLang,
  writeStoredLang,
} from "../lib/languagePreference";

// 화면 문구 자동 추출분 (scripts/i18n/codemod.js · 키 = 원문 md5 앞 8자).
// 여기 없는 키는 코드에 적힌 한국어 원문이 그대로 나온다 — 번역이 밀려도 화면은 깨지지 않는다.
// 🔒 정적 import 로 되돌리지 말 것 — 네 벌이 전부 첫 화면 번들에 들어간다. 언어마다 따로 나뉜 파일로 받는다.
const AUTO_LOADERS = {
  ko: () => import(/* webpackChunkName: "i18n-ko" */ "./locales/auto.ko.json"),
  en: () => import(/* webpackChunkName: "i18n-en" */ "./locales/auto.en.json"),
  ja: () => import(/* webpackChunkName: "i18n-ja" */ "./locales/auto.ja.json"),
  zh: () => import(/* webpackChunkName: "i18n-zh" */ "./locales/auto.zh.json"),
};

const resources = {
  ko: { translation: { ...ko } },
  en: { translation: { ...en } },
  ja: { translation: { ...ja } },
  zh: { translation: { ...zh } },
};

function deviceLanguages() {
  if (typeof navigator === "undefined") return [];
  if (navigator.languages && navigator.languages.length) return Array.from(navigator.languages);
  return navigator.language ? [navigator.language] : [];
}

/**
 * `<html lang>` 을 맞춘다. 한국어에서만 `word-break: keep-all` 이 걸리도록
 * `index.css` 가 `html[lang="ko"]` 를 본다 — 일본어·중국어에 keep-all 을 걸면 줄바꿈이 어색해진다.
 */
export function applyHtmlLang(lang) {
  try {
    document.documentElement.setAttribute("lang", HTML_LANG[lang] || lang);
  } catch (e) {
    /* SSR/테스트 환경 무시 */
  }
}

/* 테스트(jsdom)는 `navigator.languages` 가 en-US 라 영어로 열린다 — 화면 문구를 한국어로
   단언하는 기존 테스트가 기기 언어에 좌우되지 않도록 테스트 환경에서는 한국어로 고정한다. */
const initial = process.env.NODE_ENV === "test" ? { lang: DEFAULT_LANG, source: "default" } : resolveInitialLang({
  search: typeof window !== "undefined" ? window.location.search : "",
  languages: deviceLanguages(),
  stored: readStoredLang(),
});

/** 첫 화면 언어가 어디서 왔는지 — 「기기 언어에 맞춰 골랐습니다」 안내 노출 판단에 쓴다. */
let languageSource = initial.source;
export const getLanguageSource = () => languageSource;

i18n.use(initReactI18next).init({
  resources,
  lng: initial.lang,
  fallbackLng: DEFAULT_LANG,
  supportedLngs: SUPPORTED_LANGS,
  interpolation: { escapeValue: false }, // React 가 이미 이스케이프한다
  // 빈 문자열은 그대로 돌려준다 — 한국어 문장 끝의 종결 조각("예요." 등)은 다른 언어에서
  // 앞 문장이 뜻을 다 담으므로 번역을 빈 값으로 두고 화면에서 지운다.
  returnEmptyString: true,
  react: { useSuspense: false },
});

/* 자동 추출 사전 — 손으로 쓴 사전이 이긴다(overwrite=false, 예전 `{ ...auto, ...hand }` 와 같은 우선순위). */
const loadedAuto = new Set();
const pendingAuto = {};
let resourceVersion = 0;
/** 사전이 새로 들어올 때마다 1 씩 오른다 — `lazyI18n` 이 이 값으로 캐시를 버린다. */
export const getI18nResourceVersion = () => resourceVersion;

function addAutoBundle(lang, dict) {
  i18n.addResourceBundle(lang, "translation", dict, true, false);
  loadedAuto.add(lang);
  resourceVersion += 1;
}

function loadAuto(lang) {
  if (loadedAuto.has(lang) || !AUTO_LOADERS[lang]) return Promise.resolve();
  if (!pendingAuto[lang]) {
    const attempt = () => AUTO_LOADERS[lang]().then((m) => addAutoBundle(lang, m.default || m));
    // 한 번 실패하면 한 번 더 받는다. 그래도 실패하면 코드에 적힌 한국어 원문으로 그린다(화면은 깨지지 않는다).
    pendingAuto[lang] = attempt()
      .catch(attempt)
      .catch((e) => {
        console.warn(`[i18n] ${lang} 사전을 받지 못해 한국어 원문으로 표시합니다:`, e);
      })
      .finally(() => {
        delete pendingAuto[lang];
      });
  }
  return pendingAuto[lang];
}

/**
 * 그 언어의 사전 + 한국어 사전(빠진 번역은 한국어 사전으로 폴백 — 예전과 같은 결과)을 받는다.
 * 실패해도 reject 하지 않는다.
 */
export function ensureLanguageLoaded(lang) {
  const langs = Array.from(new Set([lang, DEFAULT_LANG])).filter((l) => SUPPORTED_LANGS.includes(l));
  return Promise.all(langs.map(loadAuto)).then(() => undefined);
}

if (process.env.NODE_ENV === "test") {
  // jest 는 네 벌을 바로 싣는다 — 기존 테스트가 언어를 바꾸자마자 번역을 읽는다.
  addAutoBundle("ko", require("./locales/auto.ko.json"));
  addAutoBundle("en", require("./locales/auto.en.json"));
  addAutoBundle("ja", require("./locales/auto.ja.json"));
  addAutoBundle("zh", require("./locales/auto.zh.json"));
}

/** 첫 화면 언어의 사전이 준비되면 풀린다. `src/index.js` 가 이걸 기다린 뒤 앱을 그린다. */
export const i18nReady = ensureLanguageLoaded(initial.lang);

applyHtmlLang(initial.lang);
// ?lang= 로 들어온 언어는 그 브라우저의 선택으로 남긴다(링크를 한 번 받은 뒤 계속 그 언어).
if (initial.source === "query") writeStoredLang(initial.lang);

let requestedLang = initial.lang;

/**
 * 언어를 바꾼다. `options.remember=false` 면 브라우저에 저장하지 않는다(자동 적용 등).
 * 계정 저장은 화면 쪽(`LanguageSwitcher`)이 맡는다 — 여기서는 auth 를 모른다.
 */
export function setAppLanguage(lang, { remember = true, source = "user" } = {}) {
  if (!SUPPORTED_LANGS.includes(lang)) return undefined;
  languageSource = source;
  if (remember) writeStoredLang(lang);
  // 사전을 받은 뒤 바꾼다 — 먼저 바꾸면 한국어 원문이 잠깐 보였다가 번역으로 바뀐다.
  // 연달아 바꾸면 마지막으로 고른 언어만 적용한다.
  requestedLang = lang;
  const apply = () => {
    if (requestedLang !== lang) return;
    i18n.changeLanguage(lang);
    applyHtmlLang(lang);
  };
  // 이미 받은 사전이면 예전처럼 그 자리에서 바꾸고 아무것도 돌려주지 않는다(테스트의 act() 가 동기로 끝난다).
  // 사전을 받아야 하면 다 바뀐 뒤 풀리는 Promise 를 돌려준다.
  if (loadedAuto.has(lang) && loadedAuto.has(DEFAULT_LANG)) {
    apply();
    return undefined;
  }
  return ensureLanguageLoaded(lang).then(apply);
}

export const getAppLanguage = () => i18n.language || DEFAULT_LANG;

export default i18n;
